import { apiClient } from '@/core/api/client';
import type { ReadinessBand } from './reportApi';

/**
 * Feature 110 — MVP 2 results report.
 *
 * Served alongside the V1 report rather than replacing it; an assessment's
 * stored question-set version decides which one it gets. The readiness bands
 * are shared with V1 because the four thresholds are identical in both.
 */

export type V2ReportPillar = {
  key: string;
  name: string;
  num: number;
  /** 0–100 — the mean of the pillar's five answers. */
  score: number;
};

/** What the client has marked for a priority (their own action list). */
export type V2ActionStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE' | 'DEFERRED';

export const ACTION_STATUS_LABELS: Record<V2ActionStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  COMPLETE: 'Done',
  DEFERRED: 'Later',
};

/** How soon the priority should be acted on. */
export type V2Timing = 'NOW' | 'DAYS_30' | 'MONTHS_3_6' | 'PLAN_AHEAD';

export const WHEN_LABELS: Record<V2Timing, string> = {
  NOW: 'Now',
  DAYS_30: 'Next 30 days',
  MONTHS_3_6: 'Next 3–6 months',
  PLAN_AHEAD: 'Plan ahead',
};

/** Colour only: the timing itself, and every rule behind it, comes from the payload. */
export const TIMING_TONE: Record<V2Timing, { color: string; background: string }> = {
  NOW: { color: '#b3413e', background: '#f9ecea' },
  DAYS_30: { color: '#8a5a00', background: '#fdf4e3' },
  MONTHS_3_6: { color: '#8a6414', background: '#faf0d8' },
  PLAN_AHEAD: { color: '#026857', background: '#eaf1ee' },
};

/**
 * One Start Here tool, Explore & Learn resource or expert. `href` is a relative
 * app path or an https file URL, already resolved by the backend; absent means
 * plain text. `note` is a qualifier shown after the label, never linked.
 */
export type V2SupportItem = { label: string; note?: string; href?: string };

/** One Explore & Learn resource ("Learn and discover"); `kind` was split off the label. */
export type V2LearnItem = V2SupportItem & { kind?: string };

/** One provider under "Connect for help", with its 3-min explainer videos (relative paths). */
/** A provider under Connect for help: matrix AB (primary) or AC (secondary). */
export type V2ConnectItem = V2SupportItem & {
  explainers: string[];
  role: 'primary' | 'secondary';
};

/** The sections of a need's detail after Start Here. */
export type V2NeedSection = 'connect' | 'advisor' | 'learn';

/**
 * One need on the report — the same shape in the top 3 and the timing groups.
 * Every field is approved content or a rule the backend already applied; the
 * page draws it and re-derives nothing.
 */
export type V2ReportNeed = {
  needId: string;
  title: string;
  pillarName: string;
  /** e.g. "70% Ready". */
  readinessLabel: string;
  when: V2Timing;
  why: string;
  startHere: string;
  /** The workbook button; the primary provider while the tool is not live; null for none. */
  workbook: V2SupportItem | null;
  learn: V2LearnItem[];
  /** "Ask your advisor". */
  advisorQuestion: string;
  /** "Connect for help". Empty when `sections` leaves it out. */
  connect: V2ConnectItem[];
  /** Which sections show after Start Here, in this order. */
  sections: V2NeedSection[];
  /**
   * What the client has marked — only on the top 3, the needs that take a
   * status. Keyed by Need ID server-side, so it survives a re-ranking.
   */
  status?: V2ActionStatus;
};

/** "Everything else, by timing": one group per timing that has a need, soonest first. */
export type V2TimingGroup = {
  when: V2Timing;
  /** The timing read as planning guidance. */
  guidance: string;
  /** In ranking order. */
  needs: V2ReportNeed[];
};

/** Shown over everything when an escalation check was answered Yes. */
export type V2EscalationBanner = {
  checkId: string;
  message: string;
  action: string;
  /** Relative app path (the Care Concierge). */
  href: string;
};

/**
 * The report route serves both versions, so the response is one shape or the
 * other. `timingGroups` is the discriminator: every MVP 2 report has it, empty
 * for a fully prepared client, and no V1 report does.
 */
export const isV2Report = (report: unknown): report is V2AssessmentReport =>
  typeof report === 'object' && report !== null && 'timingGroups' in report;

export type V2AssessmentReport = {
  clientName: string;
  /**
   * "Mary (your parent)": a supporter's typed first name and the relationship,
   * shown as "About …" under "Prepared for". Null for a client answering about
   * themselves, or a supporter who typed no name.
   */
  about: string | null;
  advisorName: string;
  viewerIsClient: boolean;
  completedAt: string | null;

  overallScore: number;
  category: string;
  categoryLabel: string;
  categoryDescription: string;
  readinessBands: ReadinessBand[];

  pillars: V2ReportPillar[];
  escalationBanner: V2EscalationBanner | null;
  /** "What matters most right now" — empty when every need is prepared. */
  topPriorities: V2ReportNeed[];
  timingGroups: V2TimingGroup[];
  /** One line under the report: timing is guidance, not a deadline. */
  timingNote: string;
};

/** Mark one priority started, done or deferred. Only the client may do this. */
export async function updateActionStatus(
  assessmentId: string,
  needId: string,
  status: V2ActionStatus
): Promise<{ needId: string; status: V2ActionStatus }> {
  const response = await apiClient.patch<{ needId: string; status: V2ActionStatus }>(
    `/longevity-assessments/${encodeURIComponent(assessmentId)}/priorities/${encodeURIComponent(needId)}`,
    { status }
  );
  return response.data;
}
